export type FormatEnergyPowerReturn = {
  value: number;
  originalValue: number;
  unit: string;
  formatted: string;
};

/**
 * Formats the given energy value.
 *
 * @param value - The energy value in kilowatt hours (kWh) to be formatted.
 * @param raw - Whether to return the raw value without formatting (default is false).
 * @returns The formatted energy value.
 */
export const formatEnergy = (
  value: number,
  raw?: boolean,
): FormatEnergyPowerReturn => formatEnergyPowerGeneric(value, false, raw);

/**
 * Formats the given power value.
 *
 * @param value - The power value in kilowatts (kW) to be formatted.
 * @param raw - Whether to return the raw value without formatting (default is false).
 * @returns The formatted power value.
 */
export const formatPower = (
  value: number,
  raw?: boolean,
): FormatEnergyPowerReturn => formatEnergyPowerGeneric(value, true, raw);

const MEGA = 1000; // in kilo
const GIGA = 1000000; // in kilo

const formatEnergyPowerGeneric = (
  value: number,
  power: boolean,
  raw?: boolean,
): FormatEnergyPowerReturn => {
  const unitSuffix = power ? "" : "h";
  const precision = 3;

  if (raw) {
    return {
      value: +value,
      originalValue: value,
      unit: `kW${unitSuffix}`,
      formatted: `${+value} kW${unitSuffix}`,
    };
  }

  if (value < 1) {
    return {
      value: +(value * 1000).toFixed(precision),
      originalValue: value,
      unit: `W${unitSuffix}`,
      formatted: `${+(value * 1000).toFixed(precision)} W${unitSuffix}`,
    };
  }
  if (value < MEGA) {
    return {
      value: +value.toFixed(precision),
      originalValue: value,
      unit: `kW${unitSuffix}`,
      formatted: `${+value.toFixed(precision)} kW${unitSuffix}`,
    };
  }
  if (value < GIGA) {
    return {
      value: +(value / MEGA).toFixed(precision),
      originalValue: value,
      unit: `MW${unitSuffix}`,
      formatted: `${+(value / MEGA).toFixed(precision)} MW${unitSuffix}`,
    };
  }
  return {
    value: +(value / GIGA).toFixed(precision),
    originalValue: value,
    unit: `GW${unitSuffix}`,
    formatted: `${+(value / GIGA).toFixed(precision)} GW${unitSuffix}`,
  };
};

export const calculateMVVoltage = (
  voltage: number,
  mediumVoltage: number,
): number =>
  +((((voltage * mediumVoltage) / 100) * Math.sqrt(3)) / 1000).toFixed(2);

export const calculateMVCurrent = (
  current: number,
  mediumCurrent: number,
): number => +((current * mediumCurrent) / 5).toFixed(2);

export const calculateLVVoltage = (
  voltage: number,
  mediumVoltage: number | null,
): number =>
  mediumVoltage ? +(voltage * 4).toFixed(2) : +(voltage * 1).toFixed(2);

export const calculateLVCurrent = (
  current: number,
  mediumCurrent: number,
  mediumVoltage: number | null,
): number =>
  mediumVoltage
    ? +((((current * mediumCurrent) / 5) * mediumVoltage) / 400).toFixed(2)
    : +((current * mediumCurrent) / 5).toFixed(2);
